import {
  PERMISSION_MODULES,
  PERMISSION_MODULE_PATHS,
  puedeVer,
  type PermissionModule,
  type Role,
  type RolePermissions,
} from '@alexiarest/shared';

/**
 * Que ve cada usuario en la web. La API es quien cierra de verdad (403); esto
 * solo evita ensenyar puertas cerradas y decide a donde se lleva a cada uno.
 *
 * Hay dos formas de decidirlo y conviven:
 *
 * - Los roles FIJOS de siempre, con sus reglas escritas a mano:
 *     `staff` (encargado, 21 ago 2026): sin P&L ni asistente; trabaja en Ingresos.
 *     `kitchen` (cocina, 7 sep 2026): solo la seccion Costeo; trabaja en Recetas.
 * - Un rol PERSONALIZADO (mejora 70, 8 sep 2026): manda su cuadricula de
 *   permisos y los roles fijos dejan de pintar nada.
 */
type Rol = Role | string | undefined;

/** Lo que hace falta saber de la persona para decidir que ve. */
export interface Acceso {
  role?: Rol;
  permissions?: RolePermissions;
}

/** ¿No ve la cuenta de resultados (portada, analisis, resultados, objetivos, impuestos, asistente)? */
export const sinPnl = (role: Rol): boolean => role === 'staff' || role === 'kitchen';

/** ¿Solo ve Costeo (tampoco ventas, gastos, tesoreria ni inversiones)? */
export const soloCosteo = (role: Rol): boolean => role === 'kitchen';

/** Secciones que quedan fuera para quien no ve la P&L. */
const MODULOS_PNL: PermissionModule[] = [
  'panel',
  'asistente',
  'analisis',
  'resultados',
  'objetivos',
  'impuestos',
];

/** Lo unico que ve cocina. Ajustes entra: ahi estan su cuenta y su idioma. */
const MODULOS_COSTEO: PermissionModule[] = [
  'ingredientes',
  'inventario',
  'proveedores',
  'recetas',
  'precios',
  'ajustes',
];

/**
 * ¿Puede abrir esta seccion? Es la unica pregunta que deberia hacerse el
 * menu y las guardas de ruta: asi los roles fijos y los personalizados se
 * responden en el mismo sitio y no hay dos verdades.
 */
export function veSeccion(acceso: Acceso, modulo: PermissionModule): boolean {
  if (acceso.permissions) {
    // El asistente lee la P&L: la API solo se lo abre a quien ve Resultados.
    if (modulo === 'asistente' && !puedeVer(acceso.permissions, 'resultados')) return false;
    return puedeVer(acceso.permissions, modulo);
  }
  if (soloCosteo(acceso.role)) return MODULOS_COSTEO.includes(modulo);
  if (sinPnl(acceso.role)) return !MODULOS_PNL.includes(modulo);
  return true;
}

/** Pantalla de trabajo de cada rol: a donde se le lleva si pisa una puerta cerrada. */
export function portadaDe(acceso: Acceso): string {
  if (acceso.permissions) {
    // La primera que pueda abrir, en el orden del menu. Un rol que no ve
    // nada es un rol mal montado: se le deja en Ajustes, donde al menos ve
    // su cuenta y puede pedirle al propietario que se lo amplie.
    const modulo = PERMISSION_MODULES.find((m) => puedeVer(acceso.permissions, m));
    return modulo ? PERMISSION_MODULE_PATHS[modulo] : '/ajustes';
  }
  if (acceso.role === 'kitchen') return '/recetas';
  if (acceso.role === 'staff') return '/ingresos';
  return '/';
}
